import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, useTheme } from '@mui/material'

import { TimeScaleConfig, TimelineChart } from '../../../../../components/TimelineChart/TimelineChart'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { ChartDataFormat } from '../../../../update-history/types/types'
import { LiveEventCounts } from '../../../types/LiveEventStatistics'

type LiveEventsEventCountChartProps = {
  eventCounts?: LiveEventCounts
}

export const LiveEventsEventCountChart: FC<LiveEventsEventCountChartProps> = ({ eventCounts = {} }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()

  let timeDisplayFormats: any

  switch (userLanguage) {
    case 'ja':
    case 'zh':
      timeDisplayFormats = {
        day: 'yyyy年M月d日',
        month: 'yyyy年M月',
      }
      break

    case 'en':
      timeDisplayFormats = {
        day: 'MMM d, yyyy',
        week: 'LL / yyyy',
        month: 'MMM, yyyy',
      }
      break
  }

  const datasets = [
    {
      yAxisID: 'y1',
      label: t('live-events:event_statistics_event_count_chart_y_axis_title'),
      spanGaps: false,
      tension: 0.1,
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      data: Object.entries(eventCounts).map(([ts, count]) => ({ x: +ts, y: count })),
    },
  ]

  const xScaleConfig = useMemo(() => {
    return {
      time: {
        unit: 'day',
        displayFormats: timeDisplayFormats,
        isoWeekday: true,
      },
    } as TimeScaleConfig
  }, [timeDisplayFormats])

  const yScaleConfig = {
    dataFormat: ChartDataFormat.Number,
    title: t('live-events:event_statistics_event_count_chart_y_axis_title'),
  }

  // Wrapping the chart in a Box with `aspect-ratio: 2 / 1` plus `maintainAspectRatio: false`
  // prevents the Chart.js v4 fractional-zoom shrink/expand loop and the shrink-only-on-narrow
  // bug that affects responsive charts inside flex/grid parents. (GR-2925, GR-2926)
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '2 / 1' }}>
      <TimelineChart
        chartLineOptions={{ responsive: true, maintainAspectRatio: false }}
        data={{ datasets: datasets }}
        scaleConfig={{ x: xScaleConfig, y1: yScaleConfig }}
      />
    </Box>
  )
}
